import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { monthLabel, shiftMonth } from './payoutLedgerMath';

// The publisher Billing tab's month picker (CTV-776 follow-up): previous /
// next arrows around the month name, plus a native month input for jumping.
// One month at a time is the unit a payout pays, so it is the unit the tab
// shows; the value round-trips through the URL so a pasted link reproduces
// what the sender saw, the way the admin Billing page's picker does.
interface MonthSelectorProps {
  value: string;
  onChange: (month: string) => void;
  /** Months later than this are not offered (a future delivery month has nothing to show). */
  max?: string;
}

const MonthSelector: React.FC<MonthSelectorProps> = ({ value, onChange, max }) => {
  const next = shiftMonth(value, 1);
  const nextDisabled = !!max && next > max;
  return (
    <div className="inline-flex items-center gap-1" data-testid="month-selector">
      <button
        type="button"
        onClick={() => onChange(shiftMonth(value, -1))}
        aria-label="Previous month"
        className="rounded-md border border-slate-300 bg-white p-2 text-slate-600 hover:bg-slate-50"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <label className="relative inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-900 cursor-pointer">
        <span data-testid="month-selector-label">{monthLabel(value)}</span>
        <input
          type="month"
          aria-label="Delivery month"
          value={value}
          max={max}
          onChange={(e) => e.target.value && onChange(e.target.value)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
      </label>
      <button
        type="button"
        onClick={() => onChange(next)}
        aria-label="Next month"
        disabled={nextDisabled}
        className="rounded-md border border-slate-300 bg-white p-2 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

export default MonthSelector;
